import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { SubSink } from 'subsink';
import { filter, first } from 'rxjs/operators';

import { ProductsService } from '@app/services/products/product.service';
import { LimitAccessService } from '@app/core/services/limit-access.service';
import { TagActionDialogComponent } from './tag-action-dialog.component';

interface TagSuggestion {
  name: string;
  count: number;
}

// Mirrors App\Services\ProductTags::MAX_TAG_LENGTH.
const MAX_TAG_LENGTH = 50;

/**
 * Tags on one product, as chips. Enter or a comma adds what was typed; the
 * suggestions list every tag already used in the company, and, for users
 * with the category rights, renames or deletes a tag on every product.
 *
 * Usage: <app-tags-input [tags]="form.value.prdTags" (tagsChange)="form.patchValue({ prdTags: $event })">
 */
@Component({
  selector: 'app-tags-input',
  templateUrl: './tags-input.component.html',
  styleUrls: ['./tags-input.component.scss']
})
export class TagsInputComponent implements OnInit, OnDestroy {
  @Input() tags: string[] = [];
  @Input() disabled = false;
  @Output() tagsChange = new EventEmitter<string[]>();

  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;

  readonly separatorKeysCodes = [ENTER, COMMA];
  readonly maxTagLength = MAX_TAG_LENGTH;

  text = '';
  allTags: TagSuggestion[] = [];
  canRename = false;
  canDelete = false;

  private subs = new SubSink();

  constructor(
    private _products: ProductsService,
    private _dialog: MatDialog,
    private _toastr: ToastrService,
    private _translation: TranslateService,
    private limitAccessService: LimitAccessService
  ) {}

  ngOnInit(): void {
    this.loadSuggestions();

    // Renaming or deleting a tag reaches every product, so it follows the
    // category rights (tags have none of their own).
    this.subs.add(
      this.limitAccessService.arraySettingAccess$.pipe(filter(access => !!access)).subscribe(() => {
        this.canRename = this.limitAccessService.getAccess('products', 'categories', 'edit', '');
        this.canDelete = this.limitAccessService.getAccess('products', 'categories', 'delete', '');
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // Suggestions for what is typed: tags in use, minus those already on the product.
  get suggestions(): TagSuggestion[] {
    const needle = this.text.trim().toLowerCase();
    const current = new Set((this.tags || []).map(t => t.toLowerCase()));

    return this.allTags.filter(t => !current.has(t.name.toLowerCase()) && (!needle || t.name.toLowerCase().includes(needle)));
  }

  // Offer "Add 'x'" only for text that is not already a tag somewhere.
  get canAddTyped(): boolean {
    const typed = this.cleaned(this.text);
    return !!typed && !this.allTags.some(t => t.name.toLowerCase() === typed.toLowerCase())
      && !(this.tags || []).some(t => t.toLowerCase() === typed.toLowerCase());
  }

  onTokenEnd(event: MatChipInputEvent): void {
    this.add(event.value);
    event.chipInput?.clear();
    this.text = '';
  }

  onSelected(event: MatAutocompleteSelectedEvent): void {
    this.add(event.option.value);
    this.tagInput.nativeElement.value = '';
    this.text = '';
  }

  remove(tag: string): void {
    this.emit((this.tags || []).filter(t => t !== tag));
  }

  // Backspace in an empty input takes the last chip back off.
  onBackspace(): void {
    if (!this.text && (this.tags || []).length) {
      this.emit(this.tags.slice(0, -1));
    }
  }

  rename(tag: TagSuggestion, event: Event): void {
    event.stopPropagation();
    this._dialog.open(TagActionDialogComponent, {
      data: { mode: 'rename', name: tag.name, count: tag.count },
      width: '420px',
      maxWidth: '90vw'
    }).afterClosed().subscribe((newName: string) => {
      if (!newName) {
        return;
      }
      this._products.renameTag(tag.name, newName).pipe(first()).subscribe({
        next: res => {
          if (res?.result === 'failed.access') {
            this._toastr.error(this._translation.instant('errormessage.access'));
            return;
          }
          this.emit((this.tags || []).map(t => t.toLowerCase() === tag.name.toLowerCase() ? newName : t));
          this._toastr.success(this._translation.instant('tags.renamed', { count: res?.count ?? 0 }));
          this.loadSuggestions();
        },
        error: err => this._toastr.error(this._translation.instant('tags.' + (err?.error?.message || 'saveFailed')))
      });
    });
  }

  delete(tag: TagSuggestion, event: Event): void {
    event.stopPropagation();
    this._dialog.open(TagActionDialogComponent, {
      data: { mode: 'delete', name: tag.name, count: tag.count },
      width: '420px',
      maxWidth: '90vw'
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        return;
      }
      this._products.deleteTag(tag.name).pipe(first()).subscribe({
        next: res => {
          if (res?.result === 'failed.access') {
            this._toastr.error(this._translation.instant('errormessage.access'));
            return;
          }
          this.emit((this.tags || []).filter(t => t.toLowerCase() !== tag.name.toLowerCase()));
          this._toastr.success(this._translation.instant('tags.deleted', { count: res?.count ?? 0 }));
          this.loadSuggestions();
        },
        error: () => this._toastr.error(this._translation.instant('tags.saveFailed'))
      });
    });
  }

  private add(value: string): void {
    const tag = this.cleaned(value);
    if (!tag) {
      return;
    }
    // An existing tag keeps the company's spelling ("igt" becomes "IGT").
    const known = this.allTags.find(t => t.name.toLowerCase() === tag.toLowerCase());
    const name = known ? known.name : tag;
    if ((this.tags || []).some(t => t.toLowerCase() === name.toLowerCase())) {
      return;
    }
    this.emit([...(this.tags || []), name]);
  }

  private cleaned(value: string): string {
    return (value || '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim().slice(0, MAX_TAG_LENGTH);
  }

  private emit(tags: string[]): void {
    this.tags = tags;
    this.tagsChange.emit(tags);
  }

  private loadSuggestions(): void {
    this._products.getTags().pipe(first()).subscribe({
      next: res => this.allTags = res?.tags || [],
      error: () => this.allTags = []
    });
  }
}
